// Lightweight Real User Metrics collector. Fires once when the tab is
// hidden or unloaded, with the Core Web Vitals we have measured up to
// that point. Payload goes out via navigator.sendBeacon so unloading
// doesn't cancel it. If the browser doesn't support the required
// PerformanceObserver / sendBeacon APIs, this file is a silent no-op.
//
// This is not a full RUM SDK — it's the minimum viable "before / after"
// signal to prove perf changes moved the needle. Server side receives
// it at /api/rum and just logs; upgrades to Analytics Engine or D1
// storage can layer on later without changing the wire format.
//
// This file also owns the fetch monkey-patch that emits `api.request`
// diagnostic events (via events.ts) with status + duration for every
// /api/* round-trip. Two responsibilities in one patch keeps the
// instrumentation cost to a single fetch wrap rather than layering.

import { logEvent } from "./events";

interface RumPayload {
  session: string;
  route: string;
  ua: string;
  // Core Web Vitals (approximate — see PerformanceObserver spec)
  lcp?: number; // largest contentful paint (ms)
  cls?: number; // cumulative layout shift (unitless)
  inp?: number; // interaction to next paint (ms), rolling max
  fcp?: number; // first contentful paint (ms)
  // App-level counters
  api_calls?: number;
  duration_ms?: number; // total time the page was visible before send
  // Any extra context the page wants to piggyback (e.g. lifelist size).
  ctx?: Record<string, string | number | boolean>;
}

function newSessionId(): string {
  // Not crypto-strong — this only needs to be unique enough per session
  // to correlate a stream of events. `crypto.randomUUID` where available.
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return (crypto as { randomUUID(): string }).randomUUID();
  }
  return `rum-${Math.floor(Math.random() * 1e9).toString(36)}-${Date.now().toString(36)}`;
}

let session: string | null = null;
let apiCalls = 0;
let ctx: Record<string, string | number | boolean> = {};
let observers: PerformanceObserver[] = [];
let lcp: number | undefined;
let fcp: number | undefined;
let cls = 0;
let inp = 0;
let startedAt = 0;
let sent = false;

/** Bump the API call counter — called by api.ts wrappers. */
export function rumRecordApiCall(): void {
  apiCalls += 1;
}

/** Attach a piece of extra context to the eventual payload. */
export function rumSetContext(key: string, value: string | number | boolean): void {
  ctx[key] = value;
}

interface LayoutShiftEntry extends PerformanceEntry {
  value: number;
  hadRecentInput?: boolean;
}
interface EventTimingEntry extends PerformanceEntry {
  interactionId?: number;
  duration: number;
}

function subscribe(type: string, cb: (entries: PerformanceEntry[]) => void): void {
  try {
    const obs = new PerformanceObserver((list) => cb(list.getEntries()));
    obs.observe({ type, buffered: true } as PerformanceObserverInit);
    observers.push(obs);
  } catch {
    /* type unsupported — silently drop */
  }
}

function send(): void {
  if (sent) return;
  sent = true;
  const payload: RumPayload = {
    session: session ?? "unknown",
    route: typeof location !== "undefined" ? location.pathname : "",
    ua: typeof navigator !== "undefined" ? navigator.userAgent : "",
    lcp,
    fcp,
    cls: Number(cls.toFixed(3)),
    inp,
    api_calls: apiCalls,
    duration_ms: startedAt ? Math.round(performance.now() - startedAt) : 0,
    ctx,
  };
  const body = JSON.stringify(payload);
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon("/api/rum", blob);
    } else {
      // Fall back to keepalive fetch — best-effort during unload.
      fetch("/api/rum", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    /* never let a RUM failure surface to the user */
  }
  observers.forEach((o) => o.disconnect());
  observers = [];
}

// Strip the query string off a URL for the `api.request` event's
// `path` field. Query params can carry PII (place names, coords) and
// blow up cardinality — the endpoint alone is what you actually filter
// on in the debug timeline.
function pathOnly(url: string): string {
  const q = url.indexOf("?");
  return q < 0 ? url : url.slice(0, q);
}

let apiLoggerInstalled = false;

/**
 * Install the fetch monkey-patch that emits `api.request` diagnostic
 * events on every /api/* round-trip. Split out from startRum so it can
 * run in every environment (including dev) — the /debug timeline should
 * show API traffic locally too, not just in prod. Idempotent.
 */
export function startApiEventLogger(): void {
  if (typeof window === "undefined") return;
  if (apiLoggerInstalled) return;
  apiLoggerInstalled = true;
  const origFetch = window.fetch.bind(window);
  window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
    const url =
      typeof input === "string"
        ? input
        : input instanceof URL
          ? input.pathname
          : input.url;
    const isApi = typeof url === "string" && url.startsWith("/api/");
    const isDiag =
      isApi && (url.startsWith("/api/events") || url.startsWith("/api/rum"));
    if (isApi) apiCalls += 1;
    if (!isApi || isDiag) return origFetch(input, init);
    const method = (init?.method ?? "GET").toUpperCase();
    const t0 = performance.now();
    return origFetch(input, init).then(
      (res) => {
        const fields: Record<string, string | number | boolean> = {
          method,
          path: pathOnly(url),
          status: res.status,
          ok: res.ok,
          duration_ms: Math.round(performance.now() - t0),
        };
        if (res.status === 429) {
          const ra = Number(res.headers.get("Retry-After"));
          if (Number.isFinite(ra)) fields.retry_after = ra;
        }
        logEvent("api.request", fields);
        return res;
      },
      (err: unknown) => {
        logEvent("api.request", {
          method,
          path: pathOnly(url),
          status: 0,
          ok: false,
          error: err instanceof Error ? err.message : String(err),
          duration_ms: Math.round(performance.now() - t0),
        });
        throw err;
      },
    );
  }) as typeof fetch;
}

/**
 * Start the RUM session. Idempotent — safe to call every page mount;
 * subsequent calls are ignored so we only send once per document
 * lifetime. Wire from main.tsx after the app has mounted.
 */
export function startRum(): void {
  if (typeof window === "undefined") return;
  if (session) return; // already running
  if (typeof PerformanceObserver === "undefined") return; // ancient browser
  session = newSessionId();
  startedAt = performance.now();

  // Web Vitals collection depends on PerformanceObserver; the fetch
  // monkey-patch is installed unconditionally by startApiEventLogger.
  startApiEventLogger();

  subscribe("largest-contentful-paint", (entries) => {
    const last = entries[entries.length - 1];
    if (last) lcp = Math.round(last.startTime);
  });
  subscribe("first-contentful-paint", (entries) => {
    const first = entries[0];
    if (first) fcp = Math.round(first.startTime);
  });
  subscribe("paint", (entries) => {
    for (const e of entries) {
      if (e.name === "first-contentful-paint" && fcp === undefined) {
        fcp = Math.round(e.startTime);
      }
    }
  });
  subscribe("layout-shift", (entries) => {
    for (const e of entries as LayoutShiftEntry[]) {
      if (!e.hadRecentInput) cls += e.value;
    }
  });
  subscribe("event", (entries) => {
    for (const e of entries as EventTimingEntry[]) {
      if (e.interactionId && e.duration > inp) inp = Math.round(e.duration);
    }
  });

  // Fire on visibility change (mobile Safari's most reliable unload
  // hook) and on pagehide as a belt-and-suspenders.
  const flush = () => send();
  document.addEventListener(
    "visibilitychange",
    () => {
      if (document.visibilityState === "hidden") flush();
    },
    { once: false },
  );
  window.addEventListener("pagehide", flush, { once: true });
}
